import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { JsonMapperService } from '../../../../core/services/mapper/mapper.service';
import { map, tap } from 'rxjs/operators';
import { CriteresEvaluationJure } from '../../models/criteres-evaluation-jure.model';
import { Observable } from 'rxjs';
import { EvaluationJure } from '../../models/evaluation-jure.model';
import * as moment from 'moment';
import { EvaluationJureCommission } from '../../models/evaluation-jure-commission.model';

@Injectable({
    providedIn: 'root'
})
export class EvaluationJuresService {
    constructor(private readonly http: HttpClient, private readonly mapper: JsonMapperService) {}

    /**
     * Récupère la liste des critères d'évaluation des jurés
     * @param idOrganisme - L'id de l'organisme
     */
    getCriteresEvaluationJures(idOrganisme: number): Observable<CriteresEvaluationJure[]> {
        return this.http
            .get<CriteresEvaluationJure[]>(`/api/declaration/private/organismes/${idOrganisme}/criteres-evaluation-jure`)
            .pipe(map(criteres => this.mapper.deserializeArray(criteres, CriteresEvaluationJure)));
    }

    /**
     * Récupère la liste des critères d'évaluation des jurés par leurs ids
     * @param idOrganisme - L'id de l'organisme
     * @param idsCriteres - Les ids des critères
     */
    getCriteresEvaluationJuresByIds(idOrganisme: number, idsCriteres: number[]): Observable<CriteresEvaluationJure[]> {
        return this.http
            .post<CriteresEvaluationJure[]>(`/api/declaration/private/organismes/${idOrganisme}/criteres-evaluation-jure/by-ids`, idsCriteres)
            .pipe(map(criteres => this.mapper.deserializeArray(criteres, CriteresEvaluationJure)));
    }

    /**
     * Crée une évaluation de juré
     * @param dtos - Données de l’évaluation
     */
    createEvaluationJure(dtos: EvaluationJure[]): Observable<number[]> {
        return this.http.post<number[]>('/api/declaration/private/evaluation-jure', dtos);
    }

    /**
     * Met à jour une évaluation de juré
     * @param dtos - données à mettre à jour
     */
    updateEvaluationJure(dtos: EvaluationJure[]): Observable<void> {
        return this.http.put<void>('/api/declaration/private/evaluation-jure', dtos);
    }

    /**
     * Récupère la liste des évaluations des jurés pour la commission
     * @param idJure - L'id du juré
     * @param idCritereEvaluation - L'id du critère d'évaluation
     * @param idLeurre - L'id du leurre
     * @param dateDebut - Période début pour la date de commission
     * @param dateFin - Période fin pour la date de commission
     */
    getEvaluationsJure(
        idJure?: number,
        idCritereEvaluation?: number,
        idLeurre?: number,
        dateDebut?: moment.Moment,
        dateFin?: moment.Moment
    ): Observable<EvaluationJure[]> {
        let params = new HttpParams();
        params = idJure != null ? params.set('idJure', idJure.toString()) : params;
        params = idCritereEvaluation != null ? params.set('idCritereEvaluation', idCritereEvaluation.toString()) : params;
        params = idLeurre != null ? params.set('idLeurre', idLeurre.toString()) : params;
        params = dateDebut != null ? params.set('dateDebut', dateDebut.format('YYYY-MM-DD')) : params;
        params = dateFin != null ? params.set('dateFin', dateFin.format('YYYY-MM-DD')) : params;
        return this.http
            .get<EvaluationJure[]>('/api/declaration/private/evaluation-jure', { params })
            .pipe(map(evaluations => this.mapper.deserializeArray(evaluations, EvaluationJure)));
    }

    /**
     * Récupère la liste des évaluations des jurés pour la commission
     * @param idJure - L'id du juré
     * @param idCritereEvaluation - L'id du critère d'évaluation
     * @param idLeurre - L'id du leurre
     * @param idsCahiers - La liste d'ids des chaiers des charges
     * @param dateDebut - Période début pour la date de commission
     * @param dateFin - Période fin pour la date de commission
     */
    getEvaluationsJureByCommission(
        idJure?: number,
        idCritereEvaluation?: number,
        idLeurre?: number,
        idsCahiers?: number[],
        dateDebut?: moment.Moment,
        dateFin?: moment.Moment
    ): Observable<EvaluationJureCommission[]> {
        let params = new HttpParams();
        params = idJure != null ? params.set('idJure', idJure.toString()) : params;
        params = idCritereEvaluation != null ? params.set('idCritereEvaluation', idCritereEvaluation.toString()) : params;
        params = idLeurre != null ? params.set('idLeurre', idLeurre.toString()) : params;
        params = idsCahiers != null ? params.set('idsCahiers', idsCahiers.join(',')) : params;
        params = dateDebut != null ? params.set('dateDebut', dateDebut.format('YYYY-MM-DD')) : params;
        params = dateFin != null ? params.set('dateFin', dateFin.format('YYYY-MM-DD')) : params;
        return this.http
            .get<EvaluationJureCommission[]>('/api/declaration/private/evaluation-jure/by-commission', { params })
            .pipe(map(evaluations => this.mapper.deserializeArray(evaluations, EvaluationJureCommission)));
    }

    /**
     * Récupère la liste des évaluations pour une liste de jurés
     * @param idsJures - Liste d'ids de jurés
     * @param dateDebut - Période début pour la date de commission
     * @param dateFin - Période fin pour la date de commission
     */
    getEvaluationsJures(idsJures: number[], dateDebut?: moment.Moment, dateFin?: moment.Moment): Observable<EvaluationJure[]> {
        let params = new HttpParams();
        params = idsJures != null ? params.set('idsJures', idsJures.join(',')) : params;
        params = dateDebut != null ? params.set('dateDebut', dateDebut.format('YYYY-MM-DD')) : params;
        params = dateFin != null ? params.set('dateFin', dateFin.format('YYYY-MM-DD')) : params;
        return this.http
            .get<EvaluationJure[]>('/api/declaration/private/evaluation-jure/by-jures', { params })
            .pipe(map(evaluations => this.mapper.deserializeArray(evaluations, EvaluationJure)));
    }
}
